import React, { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import api from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, Eye } from "lucide-react";
import { links } from "./SuperAdminPages";

const STATUS_STYLES = {
  received: "bg-slate-100 text-slate-600",
  verified: "bg-blue-50 text-mg-primary",
  processed: "bg-emerald-50 text-emerald-700",
  duplicate: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-600",
};

export function WebhookEventsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [viewing, setViewing] = useState(null);
  const [replayingId, setReplayingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (provider) params.append("provider", provider);
      if (status) params.append("status", status);
      const r = await api.get(`/admin/webhooks/events?${params.toString()}`);
      setRows(r.data);
    } catch (e) {
      toast.error("Failed to load webhook events");
    }
    setLoading(false);
  }, [provider, status]);

  useEffect(() => { load(); }, [load]);

  const replay = async (ev) => {
    setReplayingId(ev.webhook_event_id);
    try {
      const r = await api.post(`/admin/webhooks/events/${ev.webhook_event_id}/replay`);
      toast.success(`Replayed — new status: ${r.data.status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replay failed");
    }
    setReplayingId(null);
  };

  return (
    <DashboardLayout links={links} title="Webhook Events">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select className="mg-input text-sm" value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="webhook-filter-provider">
          <option value="">All providers</option>
          <option value="stripe">Stripe</option>
          <option value="razorpay">Razorpay</option>
        </select>
        <select className="mg-input text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="webhook-filter-status">
          <option value="">All statuses</option>
          <option value="received">received</option>
          <option value="verified">verified</option>
          <option value="processed">processed</option>
          <option value="duplicate">duplicate</option>
          <option value="failed">failed</option>
        </select>
        <button onClick={load} className="mg-btn-secondary text-sm flex items-center gap-1" data-testid="webhook-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="mg-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-5 py-3">Provider</th>
              <th className="px-5 py-3">Event Type</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Received At</th>
              <th className="px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No webhook events found</td></tr>
            )}
            {rows.map((ev) => (
              <tr key={ev.webhook_event_id} className="hover:bg-slate-50" data-testid={`webhook-row-${ev.webhook_event_id}`}>
                <td className="px-5 py-3 font-semibold text-mg-navy capitalize">{ev.provider}</td>
                <td className="px-5 py-3 font-mono text-xs text-slate-600">{ev.event_type}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_STYLES[ev.status] || "bg-slate-100 text-slate-600"}`}>{ev.status}</span>
                </td>
                <td className="px-5 py-3 text-xs text-slate-500">{ev.created_at ? new Date(ev.created_at).toLocaleString() : "—"}</td>
                <td className="px-5 py-3 flex items-center gap-3">
                  <button onClick={() => setViewing(ev)} className="text-mg-primary hover:text-mg-navy" data-testid={`view-webhook-payload-${ev.webhook_event_id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                  {ev.status === "failed" && (
                    <button
                      onClick={() => replay(ev)}
                      disabled={replayingId === ev.webhook_event_id}
                      className="text-xs font-semibold text-amber-600 hover:text-amber-800 disabled:opacity-50"
                      data-testid={`replay-webhook-${ev.webhook_event_id}`}
                    >
                      {replayingId === ev.webhook_event_id ? "Replaying…" : "Replay"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="webhook-payload-modal">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-bold text-mg-navy">{viewing.provider} — {viewing.event_type}</h3>
              <button onClick={() => setViewing(null)} className="text-slate-400 hover:text-slate-700" data-testid="webhook-payload-modal-close">✕</button>
            </div>
            <div className="space-y-2 text-sm">
              <div><span className="font-semibold text-slate-500">Status:</span> {viewing.status}</div>
              {viewing.error_message && <div className="text-red-600"><span className="font-semibold">Error:</span> {viewing.error_message}</div>}
              <div className="font-semibold text-slate-500 mt-2">Payload</div>
              <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded-lg overflow-x-auto max-h-[300px]" data-testid="webhook-payload-json">
                {JSON.stringify(viewing.payload, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
